import { useState } from 'react'
import { Box, Button, Heading, useDisclosure, useToast } from '@chakra-ui/react'
import { AddIcon } from '@chakra-ui/icons'
import OccupationModal from './OccupationModal'
import OccupationTable from './OccupationTable'
import DeleteOccupationDialog from './DeleteOccupationDialog'
import useDeleteOccupation from './useDeleteOccupation'
import { useSaveOccupation } from './useSaveOccupation'
import {
  Occupation,
  AdditionalDetails,
} from '../../../../../../types/FirestoreUser'

interface OccupationProps {
  userId: string
  additionalDetails: Partial<AdditionalDetails>
  setAdditionalDetails: React.Dispatch<
    React.SetStateAction<Partial<AdditionalDetails>>
  >
}

export default function OccupationQuestions({
  userId,
  additionalDetails,
  setAdditionalDetails,
}: OccupationProps) {
  const occupations = additionalDetails.occupations || []

  // Modal state
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentOccupation, setCurrentOccupation] = useState<Occupation | null>(
    null
  )

  // Form state
  const [occupationName, setOccupationName] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [stressLevel, setStressLevel] = useState('')

  // Validation state
  const [extraInfoRequired, setExtraInfoRequired] = useState(false)

  // Hooks
  const deleteHook = useDeleteOccupation(
    userId,
    additionalDetails,
    setAdditionalDetails
  )
  const { saveOccupation } = useSaveOccupation()
  const toast = useToast()

  const handleAddOccupation = () => {
    setCurrentOccupation(null)
    setOccupationName('')
    setStartDate('')
    setEndDate('')
    setStressLevel('')
    setExtraInfoRequired(false)
    onOpen()
  }

  const handleUpdateOccupation = (occupation: any) => {
    const typedOccupation = occupation as Occupation
    setCurrentOccupation(typedOccupation)
    setOccupationName(typedOccupation.occupationName)
    setStartDate(typedOccupation.startDate)
    setEndDate(typedOccupation.endDate || '')
    setStressLevel(typedOccupation.stressLevel)
    setExtraInfoRequired(false)
    onOpen()
  }

  const handleSave = async () => {
    // Reset any previous validation state
    setExtraInfoRequired(false)

    // Helper to check if a date from BasicDateStringPicker is empty/incomplete
    const isDateEmpty = (date: string) => {
      if (!date.trim()) return true
      return (
        date.startsWith('0-') || date.includes('-0-') || date.endsWith('-0')
      )
    }

    // Perform all validations and collect errors
    let validationError = ''

    // Validation 1: Check if occupation name exists
    if (!occupationName.trim()) {
      setExtraInfoRequired(true)
      validationError = 'Occupation name is required'
    }
    // Validation 2: Check if start date exists and is valid
    else if (isDateEmpty(startDate)) {
      setExtraInfoRequired(true)
      validationError = 'Start date is required'
    }
    // Validation 3: Check start date format
    else if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate.trim())) {
      setExtraInfoRequired(true)
      validationError = 'Start date must be in YYYY-MM-DD format'
    }
    // Validation 4: Check if start date is valid
    else if (isNaN(new Date(startDate.trim()).getTime())) {
      setExtraInfoRequired(true)
      validationError = 'Start date must be a valid date'
    }
    // Validation 5: Check end date format if provided AND not empty
    else if (
      !isDateEmpty(endDate) &&
      !/^\d{4}-\d{2}-\d{2}$/.test(endDate.trim())
    ) {
      setExtraInfoRequired(true)
      validationError = 'End date must be in YYYY-MM-DD format'
    }
    // Validation 6: Check if end date is valid if provided AND not empty
    else if (
      !isDateEmpty(endDate) &&
      isNaN(new Date(endDate.trim()).getTime())
    ) {
      setExtraInfoRequired(true)
      validationError = 'End date must be a valid date'
    }
    // Validation 7: Check if end date is after start date if provided AND not empty
    else if (
      !isDateEmpty(endDate) &&
      new Date(endDate.trim()) <= new Date(startDate.trim())
    ) {
      setExtraInfoRequired(true)
      validationError = 'End date must be after start date'
    }
    // Validation 8: Check if stress level is selected
    else if (!stressLevel.trim()) {
      setExtraInfoRequired(true)
      validationError = 'Stress level is required'
    }

    // If any validation failed, show toast and throw error
    if (validationError) {
      toast({
        title: 'Validation Error',
        description: validationError,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      throw new Error(validationError)
    }

    try {
      const occupationData: Occupation = {
        id: currentOccupation?.id || '', // Will be overridden in useSaveOccupation for new occupations
        occupationName: occupationName.trim(),
        startDate: startDate.trim(),
        stressLevel: stressLevel.trim() as any, // Type assertion for StressLevel
        ...(endDate.trim() && { endDate: endDate.trim() }),
      }

      const updatedOccupations = await saveOccupation(
        userId,
        occupationData,
        occupations,
        !!currentOccupation
      )

      setAdditionalDetails((prev: any) => ({
        ...prev,
        occupations: updatedOccupations,
      }))

      // Reset form
      setOccupationName('')
      setStartDate('')
      setEndDate('')
      setStressLevel('')
      setExtraInfoRequired(false)
      onClose()
    } catch (error) {
      // Only log non-validation errors
      const errorMessage = error instanceof Error ? error.message : ''
      if (!validationError || errorMessage !== validationError) {
        console.error('Error saving occupation:', error)
      }

      // Re-throw the error so that the modal stays open
      throw error
    }
  }

  return (
    <Box>
      <Heading as='h3' size='md' mb={4}>
        Occupation History
      </Heading>

      <Box mt={4}>
        <Button rightIcon={<AddIcon />} onClick={handleAddOccupation}>
          Add Occupation
        </Button>
      </Box>

      {occupations && occupations.length > 0 && (
        <OccupationTable
          occupations={occupations as any}
          handleUpdateOccupation={handleUpdateOccupation}
          handleDeleteOccupation={deleteHook.handleDeleteOccupation}
        />
      )}

      <OccupationModal
        id={currentOccupation?.id}
        isOpen={isOpen}
        onClose={onClose}
        occupationName={occupationName}
        setOccupationName={setOccupationName}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        stressLevel={stressLevel}
        setStressLevel={setStressLevel}
        handleSave={handleSave}
        extraInfoRequired={extraInfoRequired}
      />

      <DeleteOccupationDialog
        isOpen={deleteHook.isOpen}
        leastDestructiveRef={deleteHook.cancelRef}
        onClose={deleteHook.onClose}
        onConfirm={deleteHook.confirmDeleteOccupation}
      />
    </Box>
  )
}
